import AccountDropdown from "@/components/account-dropdown";
import GlobalSearch from "@/components/global-search";
import { ThemeSwitch } from "@/layouts/theme-switch";
import { Button, Chip, Popover, PopoverContent, PopoverTrigger, Skeleton } from "@heroui/react";
import { Bell, CalendarCheck, CalendarX, Mail, Menu, Plus, Settings, UserPlus, UserRound, Zap } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getRecentActivity as getRecentActivityApi } from "@/api/activity";
import { useSession } from "@clerk/react";
import { getCurrentWeekDateRange, getRelativeDateTimeLabel, formatDate } from "@/utils/dates";
import { Link } from "react-router-dom";

// Maps event_type → { bg, icon }
const EVENT_ICONS = {
  changed_email: { bg: "bg-primary/10", icon: <Mail size={14} className="text-primary" /> },
  accepted_invite: { bg: "bg-success/10", icon: <CalendarCheck size={14} className="text-success" /> },
  declined_invite: { bg: "bg-danger/10", icon: <CalendarX size={14} className="text-danger" /> },
  joined: { bg: "bg-accent/10", icon: <UserPlus size={14} className="text-accent" /> },
  settings_changed: { bg: "bg-warning/10", icon: <Settings size={14} className="text-warning" /> },
};
const DEFAULT_EVENT_ICON = { bg: "bg-secondary/20", icon: <Zap size={14} className="text-default-500" /> };

const TopNav = ({ onCreateMeetsClick, onCreateQuickMeetsClick, onRefresh, onMenuOpen }) => {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState();
  const [notifications, setNotifications] = useState(null);
  const { session } = useSession();

  const unreadCount = notifications?.length ?? 0;

  const handleRefreshClick = () => {
    onRefresh?.();
  };

  useEffect(() => {
    getRecentActivity();
  }, []);

  const getRecentActivity = async () => {
    const token = await session.getToken();

    getRecentActivityApi("current", getCurrentWeekDateRange(), token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setNotifications(_resp.data.Records);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex gap-2 items-center">
        <Link to={"/"}>
          <div className="rounded-full text-background nav:hidden block">
            <img src="/fav.png" width={48} alt="" />
          </div>
        </Link>
        <GlobalSearch />
      </div>

      <div className="flex gap-4 items-center">
        {/* <Button variant = 'outline' className={'bg-surface h-11'}>
          <RefreshCcw />
          Refresh
        </Button> */}
        <Button variant="outline" className={"bg-surface h-11 lg:flex hidden"} onPress={onCreateQuickMeetsClick}>
          <Zap />

          <span>Quick Loop</span>
        </Button>
        <Button className={"h-11"} onPress={onCreateMeetsClick}>
          <Plus />

          <span>New Loopp</span>
        </Button>
        <ThemeSwitch />
        <Popover placement="bottom-end" onOpenChange={setIsNotificationsOpen} isOpen={isNotificationsOpen}>
          <PopoverTrigger>
            <div className="relative">
              <Button isIconOnly radius="full" variant="secondary" className={"bg-surface"} aria-label="Notifications">
                <Bell size={18} className="text-default-500" />
              </Button>
              {unreadCount > 0 && (
                <span className="pointer-events-none bg-accent absolute -right-[2px] -top-[2px] flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[340px] p-0">
            <div className="flex items-center justify-between border-b border-default-200 px-4 py-3">
              <p className="text-sm font-semibold">Notifications</p>
              {unreadCount > 0 && (
                <Chip size="sm" color="accent" variant="primary">
                  <Chip.Label className="text-white flex gap-1">
                    <span>{unreadCount}</span>
                    <span>new</span>
                  </Chip.Label>
                </Chip>
              )}
            </div>

            <div className="max-h-[360px] divide-y divide-default-100 overflow-y-auto">
              {notifications === null ? (
                <div className="flex flex-col gap-3 px-4 py-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <Skeleton className="size-8 rounded-lg shrink-0" />
                      <div className="flex flex-col gap-1.5 flex-1">
                        <Skeleton className="h-3 w-2/3 rounded" />
                        <Skeleton className="h-3 w-full rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-default-400">No recent activity</p>
              ) : (
                notifications.map((n) => {
                  const { bg, icon } = EVENT_ICONS[n.event_type] ?? DEFAULT_EVENT_ICON;
                  return (
                    <div key={n._id} className="flex gap-3 px-4 py-3 transition-colors hover:bg-default-50">
                      <div className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${bg}`}>
                        {icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold leading-snug capitalize">
                          {n.created_by?.user_fullname ?? "System"}
                        </p>
                        <p className="mt-0.5 text-xs text-default-500 leading-snug">{n.event_full_text}</p>
                        <p className="mt-1 text-[11px] text-default-400">{formatDate(n.created_on)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="border-t border-default-200 px-4 py-2">
              <Button variant="light" size="sm" className="w-full text-xs text-primary">
                View all notifications
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        <AccountDropdown iconOnly />

        <Button isIconOnly className={"bg-surface text-foreground nav:hidden"} onPress={onMenuOpen}>
          <Menu />
        </Button>
      </div>
    </div>
  );
};

export default TopNav;
